// Sales replay API contracts (`/replay/*`, plan §6.3; DECISIONS D-036). A replay reads a
// date range of historical sales from a source connection, then replays them — with
// synthetic opening stock and production derived from the sales — into the scenario's own
// hidden replay branch, and reports coverage (lesson 12): lines processed / mapped /
// unmapped, VAT against the source's line tax, stock flags, theoretical consumption.
//
// Wire format like the stock API: snake_case, quantities as decimal strings, money as
// integer cents, instants ISO-8601 UTC, trading days `YYYY-MM-DD`.
import { z } from 'zod';
import { CHECK_STATUSES } from '../checks/index.js';
import { qtyString } from '../quantity/index.js';
import { daysBetween, isPlainDate } from '../time/index.js';
import { uomCode } from '../uom/index.js';

const instant = z.iso.datetime({ offset: true });
const plainDate = z.string().refine(isPlainDate, 'Expected a date, YYYY-MM-DD');
const count = z.int().min(0);
const cents = z.int();

/** Largest page the replay row lists return. */
export const REPLAY_PAGE_MAX = 500;
/** Page size when `limit` is not given. */
export const REPLAY_PAGE_DEFAULT = 100;
const limit = z.coerce.number().int().min(1).max(REPLAY_PAGE_MAX).default(REPLAY_PAGE_DEFAULT);
const cursor = z.string().min(1).max(512).optional();

/** The longest range one scenario may replay (a source's history is read in one snapshot). */
export const REPLAY_MAX_RANGE_DAYS = 190;

/** `:id` path parameter of replay routes. */
export const replayIdParamsSchema = z.object({ id: z.uuid() });

// ---------------------------------------------------------------------------------------
// Scenarios

/**
 * Shelf lives the replay assumes when a product has none of its own (imported products
 * usually have none): `produced` finished goods, `intermediate` products, `bought` goods
 * (no recipe) and `state_managed` products (a state rule, e.g. expiry-state SKUs).
 */
export const replayShelfLifeSchema = z.object({
  produced: z.int().min(1).max(365).default(3),
  intermediate: z.int().min(1).max(365).default(2),
  bought: z.int().min(1).max(3650).default(30),
  state_managed: z.int().min(1).max(365).default(6),
});

/** How the scenario simulates what the sales do not say. */
export const replayScenarioOptionsSchema = z.object({
  /** Planned output = sold × (1 + buffer %), produced early morning of the trading day. */
  production_buffer_pct: z.number().min(0).max(500).default(10),
  /**
   * Days of expected use that synthetic stock of bought-in goods covers, at the start and
   * whenever it runs low (the replay has no purchasing).
   */
  opening_stock_days: z.int().min(1).max(60).default(3),
  /** Seeds the synthetic ages of opening lots, so a scenario is reproducible. */
  seed: z.string().min(1).max(64).default('replay'),
  shelf_life_days: replayShelfLifeSchema.prefault({}),
  /**
   * How long after the range a sale may reach the source and still be read (offline
   * tills). The snapshot reads receipts recorded up to this many days after `to`.
   */
  late_receipt_grace_days: z.int().min(0).max(31).default(7),
});
/** Scenario options (with defaults applied). */
export type ReplayScenarioOptions = z.infer<typeof replayScenarioOptionsSchema>;

/** Longest idempotency key `POST /replay/scenarios` accepts. */
export const REPLAY_IDEMPOTENCY_KEY_MAX = 200;
/** An idempotency key: the header `Idempotency-Key` or the body's `idempotency_key`. */
export const replayIdempotencyKeySchema = z.string().trim().min(1).max(REPLAY_IDEMPOTENCY_KEY_MAX);

/**
 * `POST /replay/scenarios` body. `from`/`to` are trading days, inclusive. With an
 * idempotency key (here or in the `Idempotency-Key` header) a repeat of the same request
 * answers the scenario it created (200, no second branch); the same key with a different
 * request is refused (409 `idempotency_key_reused`).
 */
export const replayScenarioCreateRequestSchema = z
  .object({
    connection_id: z.uuid(),
    name: z.string().trim().min(1).max(120).optional(),
    from: plainDate,
    to: plainDate,
    options: replayScenarioOptionsSchema.prefault({}),
    idempotency_key: replayIdempotencyKeySchema.optional(),
  })
  .refine((b) => b.from <= b.to, { message: '`from` must not be after `to`', path: ['from'] })
  .refine((b) => b.from > b.to || daysBetween(b.from, b.to) < REPLAY_MAX_RANGE_DAYS, {
    message: `A scenario replays at most ${REPLAY_MAX_RANGE_DAYS} days`,
    path: ['to'],
  });
/** Parsed `POST /replay/scenarios` body. */
export type ReplayScenarioCreateRequest = z.infer<typeof replayScenarioCreateRequestSchema>;

/** Replay run states: queued → snapshotting → simulating → checking → completed. */
export const REPLAY_RUN_STATUSES = [
  'queued',
  'snapshotting',
  'simulating',
  'checking',
  'completed',
  'failed',
  'cancelled',
] as const;
/** One of {@link REPLAY_RUN_STATUSES}. */
export type ReplayRunStatus = (typeof REPLAY_RUN_STATUSES)[number];
/** States in which a job works on the run (one such run per scenario). */
export const REPLAY_RUN_BUSY_STATUSES = [
  'queued',
  'snapshotting',
  'simulating',
  'checking',
] as const;

/**
 * Progress steps during which the run writes to its replay branch: a run held by a job
 * cannot be cancelled then (`POST /replay/runs/:id/cancel` answers 409 `run_busy`).
 */
export const REPLAY_RUN_WRITE_STEPS = ['write', 'ledger'] as const;

/**
 * Error codes a run can end with (`error_code`), besides the source's own (`source_*`):
 * `inputs_changed` — the snapshot or the catalogue changed since the scenario last ran,
 * so re-running it would contradict what its branch already holds: nothing was written,
 * start a new scenario; `cancelled` — a person cancelled it; `run_stalled` — its worker
 * stopped (no progress and no job), failed by the reaper; also `target_mismatch`,
 * `target_not_confirmed`, `connection_off`, `connection_gone`, `internal`.
 */
export const REPLAY_RUN_ERROR_CODES = [
  'inputs_changed',
  'cancelled',
  'run_stalled',
  'target_mismatch',
  'target_not_confirmed',
  'connection_off',
  'connection_gone',
  'internal',
] as const;

/**
 * How a finished run's checks came out, counted from its report (review UI2A-1): a list
 * can then show the verdict ("3 checks failed") beside the run's state. `incomplete` =
 * `partial` + `could_not_run`.
 */
export const replayCheckTallySchema = z.object({
  total: count,
  passed: count,
  failed: count,
  incomplete: count,
});
/** A run's check tally. */
export type ReplayCheckTally = z.infer<typeof replayCheckTallySchema>;

/** Counts a report's checks by result. */
export function tallyChecks(checks: readonly { result: string }[]): ReplayCheckTally {
  const passed = checks.filter((c) => c.result === 'pass').length;
  const failed = checks.filter((c) => c.result === 'fail').length;
  return { total: checks.length, passed, failed, incomplete: checks.length - passed - failed };
}

/** Where a report's figures contradict a check that passed (should never happen). */
export const REPORT_DISAGREEMENTS = ['coverage', 'vat', 'stock', 'ledger', 'receipts'] as const;
/** One of {@link REPORT_DISAGREEMENTS}. */
export type ReportDisagreement = (typeof REPORT_DISAGREEMENTS)[number];

/** What {@link reportDisagreements} needs of a report. */
export interface DisagreementInput {
  checks: readonly { name: string; result: string }[];
  coverage: { lines: number; processed: number; unmapped: number };
  vat: { lines: number; checked: number; mismatched: number; errors?: number };
  stock: {
    sold_beyond_stock: number;
    consumed_beyond_stock: number;
    negative_rows: number;
    unmapped_lines?: number;
  };
  written: { conflicting_movements: number };
  sales: { tickets: number };
  snapshot: { receipts: number };
}

/**
 * Where a report's figures and its checks disagree (review UI2A-2, UI2A-201): a check that
 * passes while the figures show a problem, or receipts read but not replayed. The server
 * flags such a run (`figures_disagree`) and the Admin shows "Figures disagree" instead of
 * the checks' verdict, on the report and in lists.
 */
export function reportDisagreements(report: DisagreementInput): ReportDisagreement[] {
  const out: ReportDisagreement[] = [];
  const passed = (name: string) => report.checks.find((c) => c.name === name)?.result === 'pass';
  const { coverage, vat, stock } = report;
  const errors = vat.errors ?? 0;
  if (passed('replay.coverage') && (coverage.unmapped > 0 || coverage.processed < coverage.lines))
    out.push('coverage');
  if (passed('replay.vat') && (vat.mismatched + errors > 0 || vat.checked + errors < vat.lines))
    out.push('vat');
  if (
    passed('replay.stock') &&
    stock.sold_beyond_stock +
      stock.consumed_beyond_stock +
      stock.negative_rows +
      (stock.unmapped_lines ?? 0) >
      0
  )
    out.push('stock');
  if (passed('replay.ledger') && report.written.conflicting_movements > 0) out.push('ledger');
  if (report.sales.tickets !== report.snapshot.receipts) out.push('receipts');
  return out;
}

/** A run in lists. */
export const replayRunSummarySchema = z.object({
  id: z.uuid(),
  scenario_id: z.uuid(),
  status: z.enum(REPLAY_RUN_STATUSES),
  started_at: instant,
  finished_at: instant.nullable(),
  error_code: z.string().nullable(),
  error: z.string().nullable(),
  /** The report's checks, counted; `null` until the run has a report. */
  checks: replayCheckTallySchema.nullable(),
  /**
   * The report's figures contradict one of its checks ({@link reportDisagreements}; review
   * UI2A-201): a list then says "Figures disagree" instead of the checks' verdict.
   */
  figures_disagree: z.boolean().default(false),
});
/** A run in lists. */
export type ReplayRunSummary = z.infer<typeof replayRunSummarySchema>;

/** A scenario. Its branch is hidden from normal stock screens and from station sync. */
export const replayScenarioSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  connection_id: z.uuid(),
  from: plainDate,
  to: plainDate,
  options: replayScenarioOptionsSchema,
  branch: z.object({ id: z.uuid(), name: z.string(), code: z.string() }),
  /** The scenario's own channel: every replayed ticket belongs to it. */
  channel_id: z.uuid(),
  created_at: instant,
  archived_at: instant.nullable(),
  last_run: replayRunSummarySchema.nullable(),
});
/** A scenario. */
export type ReplayScenario = z.infer<typeof replayScenarioSchema>;

/** `GET /replay/scenarios` response (newest first). */
export const replayScenarioListResponseSchema = z.object({ items: z.array(replayScenarioSchema) });
/** Response of `GET /replay/scenarios`. */
export type ReplayScenarioListResponse = z.infer<typeof replayScenarioListResponseSchema>;

// ---------------------------------------------------------------------------------------
// Runs and their report

/** One check the run recorded (a `check_run` row). */
export const replayCheckSchema = z.object({
  name: z.string(),
  result: z.enum(CHECK_STATUSES),
  expected: count,
  checked: count,
  failures: count,
  /** One line with its coverage, e.g. "9 412 of 9 412 receipt lines processed; 32 unmapped". */
  summary: z.string(),
  check_run_id: z.uuid(),
});
/** A check of a run. */
export type ReplayCheck = z.infer<typeof replayCheckSchema>;

/** What the snapshot read. */
export const replaySnapshotStatsSchema = z.object({
  receipts: count,
  sales: count,
  refunds: count,
  cancelled: count,
  lines: count,
  /** Recorded by the source more than 60 s after they happened (offline tills; D-038). */
  late: count,
  /** Read (recorded in the window) but sold outside the range: kept raw, not replayed. */
  out_of_range: count,
  requests: count,
  raw_rows: count,
});

/** The run's report (lesson 12: every figure with its coverage). */
export const replayReportSchema = z.object({
  range: z.object({ from: plainDate, to: plainDate }),
  snapshot: replaySnapshotStatsSchema,
  coverage: z.object({
    lines: count,
    processed: count,
    mapped: count,
    unmapped: count,
    /** Mapped lines per mapping status (`active`, `review`, `vetoed`). */
    mapped_by_status: z.record(z.string(), count),
    summary: z.string(),
  }),
  vat: z.object({
    lines: count,
    checked: count,
    matched: count,
    mismatched: count,
    /** Lines whose VAT could not be calculated (listed with status `calculation_error`). */
    errors: count.default(0),
    /**
     * matched / **lines** × 100 by `sharePercent` (rounded down; 100 only when every line
     * agrees; review UI2A-301); null when nothing was checked.
     */
    match_pct: z.number().nullable(),
    /** Σ ours − Σ source over the checked lines. */
    total_difference_cents: cents,
    summary: z.string(),
  }),
  stock: z.object({
    /** Sale lines that moved stock (mapped, made ahead or made to order): the lines checked. */
    sale_lines: count,
    /**
     * Unmapped lines: they may have needed stock but couldn't be checked, so the stock check
     * covers `sale_lines` of `sale_lines + unmapped_lines` (review UI2A-202).
     */
    unmapped_lines: count.default(0),
    /** Mapped refund lines: they return stock, so there's nothing to run short (not counted). */
    refund_lines: count.default(0),
    /** Lines of `service` products (vouchers, fees, tips; D-043): never stock (not counted). */
    service_lines: count.default(0),
    sold_beyond_stock: count,
    consumed_beyond_stock: count,
    /** Balance rows of the replay branch below zero at the end. */
    negative_rows: count,
    summary: z.string(),
  }),
  production: z.object({
    batches: count,
    intermediate_batches: count,
    products: count,
    /** Products produced from an unsigned (draft) recipe: "unsigned recipe used for simulation". */
    unsigned_recipe_products: count,
    /** Made-to-order products sold without a recipe (nothing to explode): sold from stock like bought-in goods. */
    no_recipe_products: count,
    /** Components that could not be converted to their base unit (not consumed). */
    missing_conversions: count,
  }),
  synthetic: z.object({ opening_lots: count, restock_lots: count }),
  sales: z.object({
    tickets: count,
    cancellations: count,
    refund_lines: count,
    refund_lines_to_quarantine: count,
    /**
     * Refunded made-to-order lines (a coffee): nothing returns to stock — the product was
     * never stock and its components are in it.
     */
    refund_lines_made_to_order: count.default(0),
    state_sales: count,
    /** Lines of `service` products (vouchers, fees, tips): handled, never stock (D-043). */
    service_lines: count.default(0),
  }),
  written: z.object({
    lots: count,
    tickets: count,
    lines: count,
    payments: count,
    movements: count,
    /** Movements already in the ledger with the same fact (a re-run: harmless). */
    duplicate_movements: count,
    /** Keys already used for a different fact (never applied): the snapshot or catalogue changed. */
    conflicting_movements: count,
  }),
  timings_ms: z.object({
    snapshot: count,
    simulate: count,
    write: count,
    check: count,
  }),
  checks: z.array(replayCheckSchema),
});
/** A run's report. */
export type ReplayReport = z.infer<typeof replayReportSchema>;

/** `GET /replay/runs/:id`. */
export const replayRunDetailSchema = replayRunSummarySchema.extend({
  /** What the run is doing now. */
  progress: z.object({
    step: z.string().nullable(),
    done: count.nullable(),
    total: count.nullable(),
  }),
  report: replayReportSchema.nullable(),
});
/** A run with its progress and report. */
export type ReplayRunDetail = z.infer<typeof replayRunDetailSchema>;

// ---------------------------------------------------------------------------------------
// Row lists (large reports are rows, not one giant JSON)

/** Paged list query. */
export const replayRowsQuerySchema = z.object({ limit, cursor });
/** Parsed row list query. */
export type ReplayRowsQuery = z.infer<typeof replayRowsQuerySchema>;

/** A receipt line the replay could not map to a product. */
export const replayUnmappedLineSchema = z.object({
  id: z.uuid(),
  external_id: z.string(),
  line_ref: z.string(),
  revision: count,
  /** The source's channel (store) reference. */
  channel_ref: z.string(),
  source_item_ref: z.string(),
  source_variant_ref: z.string().nullable(),
  qty: qtyString,
  gross_cents: cents,
  occurred_at: instant,
  trading_day: plainDate,
  reason: z.enum(['no_mapping', 'unknown_channel']),
});
/** An unmapped line. */
export type ReplayUnmappedLine = z.infer<typeof replayUnmappedLineSchema>;

/**
 * How a line's VAT differs from the source's: `mismatch` (both known, different),
 * `missing_ours` / `missing_reported` (one side has none), `calculation_error` (ours could
 * not be worked out; `message` says why).
 */
export const REPLAY_VAT_MISMATCH_STATUSES = [
  'mismatch',
  'missing_ours',
  'missing_reported',
  'calculation_error',
] as const;

/** A line whose VAT differs from the source's (or is missing on one side). */
export const replayVatMismatchSchema = z.object({
  id: z.uuid(),
  external_id: z.string(),
  line_ref: z.string(),
  product: z.object({ id: z.uuid(), name: z.string() }).nullable(),
  status: z.enum(REPLAY_VAT_MISMATCH_STATUSES),
  order_type: z.string(),
  vat_code: z.string().nullable(),
  ours_cents: cents.nullable(),
  reported_cents: cents.nullable(),
  difference_cents: cents.nullable(),
  /** Why ours could not be calculated (`calculation_error` only). */
  message: z.string().nullable().default(null),
  occurred_at: instant,
});
/** A VAT mismatch. */
export type ReplayVatMismatch = z.infer<typeof replayVatMismatchSchema>;

/** How the replay treated a product. */
export const REPLAY_PRODUCT_CLASSES = ['produced', 'made_to_order', 'bought'] as const;

/**
 * One product's theoretical usage and stock over the run (base unit). Usage =
 * consumed in production + consumed at sale (made to order).
 */
export const replayConsumptionSchema = z.object({
  product: z.object({ id: z.uuid(), name: z.string(), base_uom: uomCode }),
  class: z.enum(REPLAY_PRODUCT_CLASSES),
  opening_qty: qtyString,
  restocked_qty: qtyString,
  produced_qty: qtyString,
  consumed_in_production_qty: qtyString,
  consumed_at_sale_qty: qtyString,
  /** Units of this product sold (net of nothing: cancellations are separate). */
  sold_qty: qtyString,
  cancelled_back_qty: qtyString,
  refunded_to_quarantine_qty: qtyString,
  sold_beyond_stock_qty: qtyString,
  /**
   * Used as an ingredient (in production or a made-to-order sale) beyond the stock there was
   * (review UI2A-204). `0` on runs recorded before it was kept per product.
   */
  consumed_beyond_stock_qty: qtyString.default('0'),
  /** Σ of the product's rows in the replay branch at the end of the run. */
  closing_qty: qtyString,
  /** The product's rows in the replay branch left below zero at the end (UI2A-204). */
  below_zero: z
    .array(
      z.object({
        location: z.string(),
        lot_code: z.string().nullable(),
        state: z.string(),
        qty: qtyString,
      }),
    )
    .default([]),
});
/** One product's consumption. */
export type ReplayConsumption = z.infer<typeof replayConsumptionSchema>;

/**
 * Unmapped lines grouped by what was sold where (review UI2A-7): one row per source item
 * (and variant) and store, with the names the source's catalogue gives them. Names are
 * `null` when the latest catalogue read from the source doesn't have that item or store.
 */
export const replayUnmappedItemSchema = z.object({
  source_item_ref: z.string(),
  source_variant_ref: z.string().nullable(),
  item_name: z.string().nullable(),
  variant_name: z.string().nullable(),
  channel_ref: z.string(),
  store_name: z.string().nullable(),
  reason: z.enum(['no_mapping', 'unknown_channel']),
  lines: count,
  qty: qtyString,
  gross_cents: cents,
  first_sold_at: instant,
  last_sold_at: instant,
});
/** One unmapped item. */
export type ReplayUnmappedItem = z.infer<typeof replayUnmappedItemSchema>;

/**
 * `GET /replay/runs/:id/unmapped-items`: most lines first (then value, then name). The
 * cursor is the previous page's `next_cursor`. `import_run_id` is the latest catalogue
 * import of the scenario's source (where the items get mapped), or `null`.
 */
export const replayUnmappedItemsResponseSchema = z.object({
  items: z.array(replayUnmappedItemSchema),
  next_cursor: z.string().nullable(),
  total: count,
  import_run_id: z.uuid().nullable(),
});
/** Response of the unmapped-items list. */
export type ReplayUnmappedItemsResponse = z.infer<typeof replayUnmappedItemsResponseSchema>;

/** A page of rows. */
function page<T extends z.ZodType>(item: T) {
  return z.object({ items: z.array(item), next_cursor: z.string().nullable(), total: count });
}
/** `GET /replay/runs/:id/unmapped-lines`. */
export const replayUnmappedLinesResponseSchema = page(replayUnmappedLineSchema);
/** `GET /replay/runs/:id/vat-mismatches`. */
export const replayVatMismatchesResponseSchema = page(replayVatMismatchSchema);
/** `GET /replay/runs/:id/consumption` (by product name). */
export const replayConsumptionResponseSchema = page(replayConsumptionSchema);
/** Response of the unmapped-lines list. */
export type ReplayUnmappedLinesResponse = z.infer<typeof replayUnmappedLinesResponseSchema>;
/** Response of the VAT-mismatch list. */
export type ReplayVatMismatchesResponse = z.infer<typeof replayVatMismatchesResponseSchema>;
/** Response of the consumption list. */
export type ReplayConsumptionResponse = z.infer<typeof replayConsumptionResponseSchema>;
